import { getSupabaseClient } from './supabase.client.js';

/**
 * Repositorio de `reportes_staff` (migración 34, spec staff-reportes) para el
 * panel admin. Lectura bajo RLS (sólo `is_admin()` ve filas: un `counter`
 * recibe cero) y la RPC `marcar_reporte`. El alta NO pasa por acá: la hace
 * `api/kiosco.js` con el PIN del empleado (`enviar_reporte`, service_role).
 */

const REPORTE_COLUMNS = 'id, empleado_id, texto, creado_at, leido_at, archivado_at, empleados(nombre)';

/** @param {{ message: string, code?: string }} error @returns {never} */
function rethrow(error) {
    const err = new Error(error.message);
    if (error.code) err.code = error.code;
    throw err;
}

/**
 * "Nuevos" = sin leer y sin archivar. Una sola definición para la lista y el
 * contador de la pestaña, así nunca discrepan.
 * @template Q
 * @param {Q} query
 * @returns {Q}
 */
function soloNuevos(query) {
    return query.is('leido_at', null).is('archivado_at', null);
}

/**
 * Mensajes de un filtro, más nuevos primero. Nuevos: sin leer y sin archivar;
 * Leídos: leídos y sin archivar; Archivados: archivados.
 * @param {{ filtro: 'nuevos'|'leidos'|'archivados' }} params
 * @returns {Promise<Array<object>>}
 */
export async function listReportes({ filtro }) {
    const client = getSupabaseClient();
    // Sin cliente, lanzar: una bandeja vacía parecería real (spec B3).
    if (!client) throw new Error('Supabase no configurado');
    let query = client.from('reportes_staff').select(REPORTE_COLUMNS).order('creado_at', { ascending: false });
    if (filtro === 'archivados') {
        query = query.not('archivado_at', 'is', null);
    } else if (filtro === 'leidos') {
        query = query.not('leido_at', 'is', null).is('archivado_at', null);
    } else {
        query = soloNuevos(query);
    }
    const { data, error } = await query;
    if (error) rethrow(error);
    // El orden ya lo pide la query; se repite acá para no depender de él, por
    // instante (no por texto: PostgREST omite los microsegundos cuando son 0).
    return [...(data ?? [])].sort((a, b) => Date.parse(b.creado_at) - Date.parse(a.creado_at));
}

/**
 * Cuántos mensajes Nuevos hay (para el contador de la pestaña). Lanza si la
 * lectura falla: quien llama oculta el contador en vez de mostrar un 0.
 * @returns {Promise<number>}
 */
export async function contarReportesNuevos() {
    const client = getSupabaseClient();
    if (!client) throw new Error('Supabase no configurado');
    const { count, error } = await soloNuevos(client.from('reportes_staff').select('id', { count: 'exact', head: true }));
    if (error) rethrow(error);
    return count ?? 0;
}

/**
 * @param {{ reporteId: string, accion: 'leido'|'archivado' }} params
 * @returns {Promise<{ ok: boolean, error?: string }>} el dato de la RPC (los rechazos vienen como dato)
 */
export async function marcarReporte({ reporteId, accion }) {
    const client = getSupabaseClient();
    if (!client) throw new Error('Supabase no configurado');
    const { data, error } = await client.rpc('marcar_reporte', { p_reporte_id: reporteId, p_accion: accion });
    if (error) rethrow(error);
    return data;
}
